import {
    Alert,
    Box,
    Button,
    TextField,
    Typography,
} from '@mui/material';
import { LocalizationKeys } from 'assets/localizations/localizationKeys';
import { observer } from 'mobx-react';
import React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
    PaymentTerminalBillingDetails,
    PaymentTerminalBillingDetailsSessionData,
    PaymentTerminalBillingDetailsTaxIdLookupData,
    centralServerService,
} from 'services/centralServerService';
import { useStores } from 'stores/storeInitializer';

type RecipientType = 'BUSINESS' | 'INDIVIDUAL';
type CuiVerificationError = 'not_found' | 'unavailable' | null;
type BillingDetailsError = 'submit' | 'lookup' | null;

interface BillingDetailsFormState {
    recipientType: RecipientType;
    taxCountryCode: string;
    taxId: string;
    name: string;
    address: string;
    email: string;
}

const DEFAULT_TAX_COUNTRIES = [
    'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GR',
    'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO',
    'SE', 'SI', 'SK', 'XI',
];

const normalizeCountryCode = (countryCode?: string | null): string | undefined => {
    const normalized = countryCode?.trim().toUpperCase();
    return normalized || undefined;
};

const normalizeTaxIdValue = (taxId?: string | null): string => (
    taxId || ''
).replace(/\s/g, '').toUpperCase();

const normalizeRomanianCui = (cui?: string | null): string | null => {
    const value = normalizeTaxIdValue(cui);
    const digits = value.startsWith('RO') ? value.slice(2) : value;
    return /^\d{2,10}$/.test(digits) && Number(digits) > 0 ? digits : null;
};

const isValidRomanianCui = (cui?: string | null): boolean => {
    const digits = normalizeRomanianCui(cui);
    if (!digits) {
        return false;
    }
    const padded = digits.padStart(10, '0');
    const weights = [7, 5, 3, 2, 1, 7, 5, 3, 2];
    const sum = weights.reduce(
        (total, weight, index) => total + Number(padded[index]) * weight,
        0,
    );
    const remainder = (sum * 10) % 11;
    const expectedControlDigit = remainder === 10 ? 0 : remainder;
    return expectedControlDigit === Number(padded[9]);
};

const stripMatchingCountryPrefix = (taxCountryCode: string, taxId: string): string => {
    const prefix = taxId.slice(0, 2);
    if (prefix === taxCountryCode || (taxCountryCode === 'GR' && prefix === 'EL')) {
        return taxId.slice(2);
    }
    return taxId;
};

const buildNormalizedTaxId = (taxCountryCode: string, nationalTaxId: string): string | undefined => {
    if (!taxCountryCode || !nationalTaxId) {
        return undefined;
    }
    return `${taxCountryCode}${nationalTaxId}`;
};

const normalizeLookupTaxIdForSelectedCountry = (
    taxCountryCode: string,
    normalizedTaxId?: string,
): string | undefined => {
    const value = normalizeTaxIdValue(normalizedTaxId);
    if (!value) {
        return undefined;
    }
    if (taxCountryCode === 'GR' && value.startsWith('EL')) {
        return `GR${value.slice(2)}`;
    }
    if (taxCountryCode === 'RO' && value.startsWith('RO')) {
        return value.slice(2);
    }
    return value;
};

const hasValues = (value: Record<string, unknown>): boolean =>
    Object.values(value).some((entry) => entry !== undefined && entry !== null && entry !== '');

const buildBillingDetails = (
    form: BillingDetailsFormState,
    taxLookup?: PaymentTerminalBillingDetailsTaxIdLookupData | null,
): PaymentTerminalBillingDetails => {
    const taxCountryCode = normalizeCountryCode(form.taxCountryCode);
    const taxId = normalizeTaxIdValue(form.taxId);
    const nationalTaxId = taxCountryCode ? stripMatchingCountryPrefix(taxCountryCode, taxId) : taxId;
    const name = form.name.trim();
    const address = form.address.trim();
    const email = form.email.trim();
    const recipient = {
        type: form.recipientType,
        ...(name ? { name } : {}),
        ...(taxCountryCode && nationalTaxId ? {
            taxCountryCode,
            taxId: nationalTaxId,
            normalizedTaxId: taxCountryCode === 'RO' && form.recipientType === 'BUSINESS'
                ? taxLookup?.normalizedTaxId
                : buildNormalizedTaxId(taxCountryCode, nationalTaxId),
        } : {}),
    };
    const details: PaymentTerminalBillingDetails = { schemaVersion: 1 };
    if (hasValues(recipient) && (name || nationalTaxId)) {
        details.recipient = recipient;
    }
    if (address) {
        details.address = { rawAddress: address };
    }
    if (email) {
        details.contact = { email };
    }
    return details;
};

// Form and request semantics from production 0602da7657e138fe9dd253919fb6b68e16df75b6.
// The route supplies the already loaded session so contract selection and defaults use one response.
export const ProductionBillingDetails: React.FC<{
    sessionContext: PaymentTerminalBillingDetailsSessionData;
}> = observer(({ sessionContext }) => {
    const { tenantId, token } = useParams<{
        tenantId: string;
        token: string;
    }>();
    const location = useLocation();
    const searchParams = React.useMemo(
        () => new URLSearchParams(location.search),
        [location.search],
    );
    const verificationToken = searchParams.get('verificationToken');
    const { store } = useStores();
    const [form, setForm] = React.useState<BillingDetailsFormState>({
        recipientType: 'BUSINESS',
        taxCountryCode: '',
        taxId: '',
        name: '',
        address: '',
        email: '',
    });
    const [submitting, setSubmitting] = React.useState(false);
    const [lookingUp, setLookingUp] = React.useState(false);
    const [submitted, setSubmitted] = React.useState(false);
    const [error, setError] = React.useState<BillingDetailsError>(null);
    const [cuiVerificationError, setCuiVerificationError] =
        React.useState<CuiVerificationError>(null);
    const [taxLookup, setTaxLookup] =
        React.useState<PaymentTerminalBillingDetailsTaxIdLookupData | null>(null);
    const recipientTypeEdited = React.useRef(false);
    const taxCountryEdited = React.useRef(false);
    const lookupGeneration = React.useRef(0);

    const policyForm = sessionContext?.billingDetailsCollection?.form;
    const recipientTypes = React.useMemo(
        () => (
            policyForm?.recipientTypes?.length
                ? policyForm.recipientTypes
                : ['BUSINESS', 'INDIVIDUAL'] as RecipientType[]
        ),
        [policyForm],
    );
    const allowedTaxCountryCodes = React.useMemo(
        () => (
            policyForm?.allowedTaxCountryCodes?.length
                ? policyForm.allowedTaxCountryCodes.map((countryCode) => countryCode.toUpperCase())
                : DEFAULT_TAX_COUNTRIES
        ),
        [policyForm],
    );
    const lookupEnabled = policyForm?.lookup?.enabled === true;

    React.useEffect(() => {
        if (!policyForm) {
            return;
        }
        setForm((current) => {
            const configuredRecipientType = policyForm.defaultRecipientType as RecipientType | undefined;
            const defaultRecipientType = configuredRecipientType && recipientTypes.includes(configuredRecipientType)
                ? configuredRecipientType
                : recipientTypes[0] || 'BUSINESS';
            const nextRecipientType = !recipientTypeEdited.current
                ? defaultRecipientType
                : recipientTypes.includes(current.recipientType)
                    ? current.recipientType
                    : defaultRecipientType;
            const configuredTaxCountry = normalizeCountryCode(policyForm.defaultTaxCountryCode);
            const defaultTaxCountry = configuredTaxCountry && allowedTaxCountryCodes.includes(configuredTaxCountry)
                ? configuredTaxCountry
                : allowedTaxCountryCodes[0] || '';
            const nextTaxCountry = !taxCountryEdited.current
                ? defaultTaxCountry
                : allowedTaxCountryCodes.includes(current.taxCountryCode)
                    ? current.taxCountryCode
                    : defaultTaxCountry;
            return {
                ...current,
                recipientType: nextRecipientType as RecipientType,
                taxCountryCode: nextTaxCountry,
            };
        });
    }, [policyForm, recipientTypes, allowedTaxCountryCodes]);

    const updateField =
        (field: keyof BillingDetailsFormState) =>
            (event: React.ChangeEvent<HTMLInputElement>) => {
                if (field === 'taxId' || field === 'taxCountryCode' || field === 'recipientType') {
                    lookupGeneration.current++;
                    setTaxLookup(null);
                    setCuiVerificationError(null);
                    setLookingUp(false);
                }
                if (field === 'recipientType') {
                    recipientTypeEdited.current = true;
                } else if (field === 'taxCountryCode') {
                    taxCountryEdited.current = true;
                }
                setForm((current) => ({
                    ...current,
                    [field]: event.target.value,
                }));
            };

    const submitBillingDetails = async (billingDetails: PaymentTerminalBillingDetails) => {
        if (!tenantId || !token) {
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            await centralServerService.submitPaymentTerminalBillingDetails(
                tenantId,
                token,
                verificationToken,
                billingDetails,
            );
            setSubmitted(true);
        } catch {
            setError('submit');
        } finally {
            setSubmitting(false);
        }
    };

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        await submitBillingDetails(buildBillingDetails(form, taxLookup));
    };

    const continueWithoutBillingDetails = async () => {
        await submitBillingDetails({ schemaVersion: 1 });
    };

    const isRomanianBusiness =
        form.recipientType === 'BUSINESS' &&
        form.taxCountryCode === 'RO';
    const hasRomanianCui = !!form.taxId.trim();
    const romanianCuiLocallyValid =
        !isRomanianBusiness || (hasRomanianCui && isValidRomanianCui(form.taxId));
    const canLookup =
        lookupEnabled &&
        form.recipientType === 'BUSINESS' &&
        !!form.taxId.trim() &&
        allowedTaxCountryCodes.includes(form.taxCountryCode) &&
        romanianCuiLocallyValid;
    const verifiedRomanianCui =
        taxLookup?.canContinue !== false &&
        taxLookup?.primaryProvider === 'anaf' &&
        taxLookup?.identityStatus === 'verified' &&
        normalizeLookupTaxIdForSelectedCountry('RO', taxLookup.normalizedTaxId) ===
            normalizeLookupTaxIdForSelectedCountry('RO', form.taxId);
    const canSubmit =
        !isRomanianBusiness || (romanianCuiLocallyValid && verifiedRomanianCui);

    const lookupTaxId = async () => {
        if (!tenantId || !token || !canLookup) {
            return;
        }
        const generation = ++lookupGeneration.current;
        const requestIdentity = {
            taxCountryCode: form.taxCountryCode,
            taxId: form.taxId.trim(),
            recipientType: form.recipientType,
        };
        setLookingUp(true);
        setError(null);
        setCuiVerificationError(null);
        try {
            const result =
                await centralServerService.lookupPaymentTerminalBillingDetailsTaxId(
                    tenantId,
                    token,
                    verificationToken,
                    requestIdentity,
                );
            if (generation !== lookupGeneration.current) {
                return;
            }
            const lookupResult = result || {};
            const normalizedTaxId = normalizeLookupTaxIdForSelectedCountry(
                requestIdentity.taxCountryCode,
                lookupResult.normalizedTaxId,
            );
            setTaxLookup(lookupResult);
            const lookupRejected =
                requestIdentity.taxCountryCode === 'RO' &&
                requestIdentity.recipientType === 'BUSINESS' &&
                (
                    lookupResult.canContinue === false ||
                    lookupResult.primaryProvider !== 'anaf' ||
                    lookupResult.identityStatus !== 'verified'
                );
            setCuiVerificationError(lookupRejected
                ? lookupResult.identityStatus === 'temporarily_unavailable'
                    ? 'unavailable'
                    : 'not_found'
                : null);
            if (lookupRejected) {
                return;
            }
            setForm((current) => ({
                ...current,
                taxId: normalizedTaxId || current.taxId,
                name: lookupResult.autofill?.legalName || current.name,
                address: lookupResult.autofill?.rawAddress || current.address,
            }));
        } catch {
            if (generation === lookupGeneration.current) {
                setError('lookup');
                setTaxLookup(null);
                setCuiVerificationError(null);
            }
        } finally {
            if (generation === lookupGeneration.current) {
                setLookingUp(false);
            }
        }
    };

    return (
        <Box
            component="form"
            onSubmit={submit}
            display="flex"
            flexDirection="column"
            gap={2}
            p={3}
            maxWidth={520}
            mx="auto"
        >
            <Typography variant="h5">
                {store.L(LocalizationKeys.BillingDetailsTitle)}
            </Typography>
            <Typography color="text.secondary">
                {store.L(LocalizationKeys.BillingDetailsBody)}
            </Typography>
            <TextField
                select
                SelectProps={{ native: true }}
                label={store.L(LocalizationKeys.BillingDetailsRecipientType)}
                value={form.recipientType}
                onChange={updateField('recipientType')}
                fullWidth
            >
                {recipientTypes.map((recipientType) => (
                    <option key={recipientType} value={recipientType}>
                        {recipientType === 'BUSINESS'
                            ? store.L(LocalizationKeys.BillingDetailsRecipientBusiness)
                            : store.L(LocalizationKeys.BillingDetailsRecipientIndividual)}
                    </option>
                ))}
            </TextField>
            <TextField
                select
                SelectProps={{ native: true }}
                label={store.L(LocalizationKeys.BillingDetailsTaxCountry)}
                value={form.taxCountryCode}
                onChange={updateField('taxCountryCode')}
                fullWidth
            >
                {allowedTaxCountryCodes.map((countryCode) => (
                    <option key={countryCode} value={countryCode}>
                        {countryCode}
                    </option>
                ))}
            </TextField>
            <TextField
                label={store.L(LocalizationKeys.BillingDetailsTaxId)}
                value={form.taxId}
                onChange={updateField('taxId')}
                fullWidth
                error={(isRomanianBusiness && hasRomanianCui && !romanianCuiLocallyValid) || !!cuiVerificationError}
                helperText={isRomanianBusiness && !romanianCuiLocallyValid
                    ? store.L(LocalizationKeys.BillingDetailsCuiFormatInvalid)
                    : cuiVerificationError === 'not_found'
                        ? store.L(LocalizationKeys.BillingDetailsCuiInvalid)
                        : cuiVerificationError === 'unavailable'
                            ? store.L(LocalizationKeys.BillingDetailsAnafUnavailable)
                    : isRomanianBusiness && !verifiedRomanianCui
                        ? store.L(LocalizationKeys.BillingDetailsCuiVerificationRequired)
                        : undefined}
            />
            <Button
                type="button"
                variant="outlined"
                disabled={!canLookup || lookingUp || submitted}
                onClick={lookupTaxId}
            >
                {store.L(LocalizationKeys.BillingDetailsFind)}
            </Button>
            <TextField
                label={store.L(LocalizationKeys.BillingDetailsName)}
                value={form.name}
                onChange={updateField('name')}
                fullWidth
            />
            <TextField
                label={store.L(LocalizationKeys.BillingDetailsAddress)}
                value={form.address}
                onChange={updateField('address')}
                fullWidth
                multiline
                minRows={3}
            />
            <TextField
                label={store.L(LocalizationKeys.BillingDetailsEmail)}
                value={form.email}
                onChange={updateField('email')}
                fullWidth
                type="email"
            />
            <Button
                type="submit"
                variant="contained"
                disabled={submitting || submitted || !canSubmit}
            >
                {store.L(LocalizationKeys.BillingDetailsSubmit)}
            </Button>
            <Button
                type="button"
                variant="outlined"
                disabled={submitting || submitted}
                onClick={continueWithoutBillingDetails}
            >
                {store.L(LocalizationKeys.BillingDetailsContinueWithout)}
            </Button>
            {submitted ? (
                <Alert severity="success">
                    {store.L(LocalizationKeys.BillingDetailsSubmitted)}
                </Alert>
            ) : null}
            {error ? (
                <Alert severity="error">
                    {store.L(error === 'lookup'
                        ? LocalizationKeys.BillingDetailsLookupError
                        : LocalizationKeys.BillingDetailsError)}
                </Alert>
            ) : null}
        </Box>
    );
});

export default ProductionBillingDetails;
